import {
  RestaurantOrderItemDTO,
  RestaurantOrderServiceType,
  RestaurantOrderStatus,
} from '../../../types/restaurant/RestaurantOrderTypes';
import { RestaurantTableStatus } from '../../../types/restaurant/RestaurantTableTypes';

export type OrderMobileStep = 'TABLES' | 'MENU' | 'ORDER';

export const tableStatus: Record<
  RestaurantTableStatus,
  {
    label: string;
    classes: string;
    dot: string;
    /** color del ícono de la mesa */
    icon: string;
    /** fondo tenue de la "superficie" de la mesa */
    surface: string;
    /** pill de estado */
    chip: string;
  }
> = {
  FREE: {
    label: 'Libre',
    classes: 'border-success/40 bg-success/5 hover:border-success',
    dot: 'bg-success',
    icon: 'text-success',
    surface: 'bg-success/10',
    chip: 'bg-success/15 text-success',
  },
  OCCUPIED: {
    label: 'Ocupada',
    classes: 'border-danger/40 bg-danger/5 hover:border-danger',
    dot: 'bg-danger',
    icon: 'text-danger',
    surface: 'bg-danger/10',
    chip: 'bg-danger/15 text-danger',
  },
  RESERVED: {
    label: 'Reservada',
    classes: 'border-warning/50 bg-warning/5',
    dot: 'bg-warning',
    icon: 'text-warning',
    surface: 'bg-warning/10',
    chip: 'bg-warning/15 text-warning',
  },
  CLEANING: {
    label: 'Limpieza',
    classes: 'border-primary/40 bg-primary/5',
    dot: 'bg-primary',
    icon: 'text-primary',
    surface: 'bg-primary/10',
    chip: 'bg-primary/15 text-primary',
  },
  OUT_OF_SERVICE: {
    label: 'Fuera de servicio',
    classes:
      'border-stroke bg-gray-2 opacity-60 dark:border-strokedark dark:bg-meta-4',
    dot: 'bg-bodydark2',
    icon: 'text-bodydark2',
    surface: 'bg-gray-2 dark:bg-meta-4',
    chip: 'bg-gray-2 text-bodydark2 dark:bg-meta-4',
  },
};

export const orderLabels: Record<RestaurantOrderStatus, string> = {
  OPEN: 'Abierta',
  CONFIRMED: 'Confirmada',
  IN_KITCHEN: 'En cocina',
  READY: 'Lista',
  BILLING: 'En cobro',
  CANCELLED: 'Cancelada',
  CLOSED: 'Cerrada',
};

export const serviceTypeLabels: Record<RestaurantOrderServiceType, string> = {
  DINE_IN: 'Mesa',
  TAKEAWAY: 'Para llevar',
  DELIVERY: 'Domicilio',
};

/** Mismo lenguaje visual que `tableStatus`, aplicado a las tarjetas de
 * órdenes sin mesa (Para llevar / Domicilio) en vez de al estado de la mesa. */
export const orderCardStatus: Record<
  RestaurantOrderStatus,
  {
    label: string;
    classes: string;
    dot: string;
    icon: string;
    surface: string;
    chip: string;
  }
> = {
  OPEN: {
    label: 'Abierta',
    classes: 'border-primary/40 bg-primary/5 hover:border-primary',
    dot: 'bg-primary',
    icon: 'text-primary',
    surface: 'bg-primary/10',
    chip: 'bg-primary/15 text-primary',
  },
  CONFIRMED: {
    label: 'Confirmada',
    classes: 'border-success/40 bg-success/5 hover:border-success',
    dot: 'bg-success',
    icon: 'text-success',
    surface: 'bg-success/10',
    chip: 'bg-success/15 text-success',
  },
  IN_KITCHEN: {
    label: 'En cocina',
    classes: 'border-warning/40 bg-warning/5 hover:border-warning',
    dot: 'bg-warning',
    icon: 'text-warning',
    surface: 'bg-warning/10',
    chip: 'bg-warning/15 text-warning',
  },
  READY: {
    label: 'Lista',
    classes: 'border-success/40 bg-success/5 hover:border-success',
    dot: 'bg-success',
    icon: 'text-success',
    surface: 'bg-success/10',
    chip: 'bg-success/15 text-success',
  },
  BILLING: {
    label: 'En cobro',
    classes: 'border-primary/40 bg-primary/5 hover:border-primary',
    dot: 'bg-primary',
    icon: 'text-primary',
    surface: 'bg-primary/10',
    chip: 'bg-primary/15 text-primary',
  },
  CANCELLED: {
    label: 'Cancelada',
    classes: 'border-danger/40 bg-danger/5',
    dot: 'bg-danger',
    icon: 'text-danger',
    surface: 'bg-danger/10',
    chip: 'bg-danger/15 text-danger',
  },
  CLOSED: {
    label: 'Cerrada',
    classes:
      'border-stroke bg-gray-2 opacity-60 dark:border-strokedark dark:bg-meta-4',
    dot: 'bg-bodydark2',
    icon: 'text-bodydark2',
    surface: 'bg-gray-2 dark:bg-meta-4',
    chip: 'bg-gray-2 text-bodydark2 dark:bg-meta-4',
  },
};

export const orderItemName = (item: RestaurantOrderItemDTO) =>
  item.menuItemName ||
  item.itemName ||
  item.productName ||
  `Ítem #${item.menuItemId}`;

export const formatOrderMoney = (value?: number) =>
  new Intl.NumberFormat('es-SV', {
    style: 'currency',
    currency: 'USD',
  }).format(Number(value ?? 0));
